/**
 * Platform Utility Functions
 * Comprehensive platform detection and utility functions for MyQampus PWA
 */

// Check if Capacitor is available
let Capacitor = null
try {
  Capacitor = require('@capacitor/core').Capacitor
} catch (e) {
  // Capacitor not installed or not available
}

/**
 * Check if running in Capacitor native app
 * @returns {boolean} True if running in Capacitor
 */
export const isCapacitor = () => {
  return Capacitor && Capacitor.isNativePlatform ? Capacitor.isNativePlatform() : false
}

/**
 * Get the user agent string
 * @returns {string} User agent string
 */
export const getUserAgent = () => {
  return navigator.userAgent || navigator.vendor || window.opera || ''
}

/**
 * Check if the device is iOS (iPhone, iPad, iPod)
 * @returns {boolean} True if iOS device
 */
export const isIOS = () => {
  const userAgent = getUserAgent()
  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

/**
 * Check if the device is Android
 * @returns {boolean} True if Android device
 */
export const isAndroid = () => {
  const userAgent = getUserAgent()
  return /Android/.test(userAgent)
}

/**
 * Check if the device is a mobile device (iOS or Android)
 * @returns {boolean} True if mobile device
 */
export const isMobile = () => {
  return isIOS() || isAndroid()
}

/**
 * Check if the device is desktop
 * @returns {boolean} True if desktop device
 */
export const isDesktop = () => {
  return !isMobile()
}

/**
 * Check if the device is a tablet
 * @returns {boolean} True if tablet device
 */
export const isTablet = () => {
  const userAgent = getUserAgent()
  return (
    /iPad|Android(?!.*Mobile)|Tablet/.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

/**
 * Check if the device is a phone
 * @returns {boolean} True if phone device
 */
export const isPhone = () => {
  return isMobile() && !isTablet()
}

/**
 * Get the specific iOS version
 * @returns {number|null} iOS version number or null if not iOS
 */
export const getIOSVersion = () => {
  if (!isIOS()) return null

  const userAgent = getUserAgent()
  const match = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/)
  if (match) {
    return parseInt(match[1], 10)
  }
  return null
}

/**
 * Get the specific Android version
 * @returns {number|null} Android version number or null if not Android
 */
export const getAndroidVersion = () => {
  if (!isAndroid()) return null

  const userAgent = getUserAgent()
  const match = userAgent.match(/Android (\d+\.\d+)/)
  if (match) {
    return parseFloat(match[1])
  }
  return null
}

/**
 * Check if the browser is Safari
 * @returns {boolean} True if Safari browser
 */
export const isSafari = () => {
  const userAgent = getUserAgent()
  return /Safari/.test(userAgent) && !/Chrome/.test(userAgent)
}

/**
 * Check if the browser is Chrome
 * @returns {boolean} True if Chrome browser
 */
export const isChrome = () => {
  const userAgent = getUserAgent()
  return /Chrome/.test(userAgent) && !/Edge/.test(userAgent)
}

/**
 * Check if the browser is Firefox
 * @returns {boolean} True if Firefox browser
 */
export const isFirefox = () => {
  const userAgent = getUserAgent()
  return /Firefox/.test(userAgent)
}

/**
 * Check if the browser is Edge
 * @returns {boolean} True if Edge browser
 */
export const isEdge = () => {
  const userAgent = getUserAgent()
  return /Edge/.test(userAgent)
}

/**
 * Check if the browser supports PWA installation
 * @returns {boolean} True if PWA installation is supported
 */
export const supportsPWAInstallation = () => {
  return (
    'serviceWorker' in navigator &&
    ('BeforeInstallPromptEvent' in window || (isIOS() && isSafari()) || (isAndroid() && isChrome()))
  )
}

/**
 * Check if the app is running as a PWA (installed) or Capacitor app
 * @returns {boolean} True if running as installed PWA or Capacitor app
 */
export const isRunningAsPWA = () => {
  // Capacitor apps are essentially "installed" apps
  if (isCapacitor()) {
    return true
  }
  // Original PWA detection
  return (
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
  )
}

/**
 * Get platform-specific installation instructions
 * @returns {object} Object containing title, description, and steps
 */
export const getInstallInstructions = () => {
  if (isIOS()) {
    return {
      title: 'Install MyQampus on iOS',
      description:
        'Install MyQampus on your iOS device for quick access and offline functionality.',
      steps: [
        'Open Safari browser',
        'Tap the Share button',
        'Select "Add to Home Screen"',
        'Tap "Add" to confirm',
      ],
      icon: 'ios',
      browser: 'Safari',
    }
  } else if (isAndroid()) {
    return {
      title: 'Install MyQampus on Android',
      description:
        'Install MyQampus on your Android device for quick access and offline functionality.',
      steps: [
        'Open Chrome browser',
        'Tap the menu button (⋮)',
        'Select "Add to Home Screen"',
        'Tap "Add" to confirm',
      ],
      icon: 'android',
      browser: 'Chrome',
    }
  } else {
    // Desktop instructions based on browser
    if (isChrome()) {
      return {
        title: 'Install MyQampus on Desktop (Chrome)',
        description:
          'Install MyQampus on your desktop using Chrome for quick access and enhanced features.',
        steps: [
          'Look for the install icon (⬇️) in the address bar',
          'Or click the three-dot menu (⋮) in the top-right corner',
          'Select "Install MyQampus" from the menu',
          'Click "Install" in the popup dialog',
        ],
        icon: 'desktop',
        browser: 'Chrome',
      }
    } else if (isSafari()) {
      return {
        title: 'Install MyQampus on Desktop (Safari)',
        description:
          'Install MyQampus on your desktop using Safari for quick access and enhanced features.',
        steps: [
          'Click "File" in the top menu bar',
          'Or use the Share button in the toolbar',
          'Select "Add to Dock" or "Add to Desktop"',
        ],
        icon: 'desktop',
        browser: 'Safari',
      }
    } else if (isFirefox()) {
      return {
        title: 'Install MyQampus on Desktop (Firefox)',
        description:
          'Install MyQampus on your desktop using Firefox for quick access and enhanced features.',
        steps: [
          'Look for the install icon (⬇️) in the address bar',
          'Or click the menu button (☰) in the top-right corner',
          'Select "Install App" from the menu',
          'Click "Install" in the popup dialog',
        ],
        icon: 'desktop',
        browser: 'Firefox',
      }
    } else if (isEdge()) {
      return {
        title: 'Install MyQampus on Desktop (Edge)',
        description:
          'Install MyQampus on your desktop using Edge for quick access and enhanced features.',
        steps: [
          'Look for the install icon (⬇️) in the address bar',
          'Or click the three-dot menu (⋯) in the top-right corner',
          'Select "Apps" then "Install this site as an app"',
          'Click "Install" in the popup dialog',
        ],
        icon: 'desktop',
        browser: 'Edge',
      }
    } else {
      // Generic desktop instructions for other browsers
      return {
        title: 'Install MyQampus on Desktop',
        description: 'Install MyQampus on your desktop for quick access and enhanced features.',
        steps: [
          "Look for the install button in your browser's address bar",
          'Or use the browser menu to find "Install App" or "Add to Home Screen"',
          'Click "Install" to add to your desktop',
          'If not available, try using Chrome, Firefox, or Edge for better PWA support',
        ],
        icon: 'desktop',
        browser: 'Any modern browser',
      }
    }
  }
}

/**
 * Get platform-specific browser recommendations
 * @returns {string} Recommended browser for the platform
 */
export const getRecommendedBrowser = () => {
  if (isIOS()) {
    return 'Safari'
  } else if (isAndroid()) {
    return 'Chrome'
  } else {
    return 'Chrome, Firefox, or Edge'
  }
}

/**
 * Check if the device supports touch events
 * @returns {boolean} True if touch is supported
 */
export const supportsTouch = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

/**
 * Get device orientation
 * @returns {string} 'portrait' or 'landscape'
 */
export const getDeviceOrientation = () => {
  return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
}

/**
 * Check if the device is in portrait mode
 * @returns {boolean} True if portrait orientation
 */
export const isPortrait = () => {
  return getDeviceOrientation() === 'portrait'
}

/**
 * Check if the device is in landscape mode
 * @returns {boolean} True if landscape orientation
 */
export const isLandscape = () => {
  return getDeviceOrientation() === 'landscape'
}

/**
 * Get screen dimensions
 * @returns {object} Object with width and height
 */
export const getScreenDimensions = () => {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: window.devicePixelRatio || 1,
  }
}

/**
 * Check if the device has a high DPI display
 * @returns {boolean} True if high DPI display
 */
export const hasHighDPIDisplay = () => {
  return window.devicePixelRatio > 1
}

/**
 * Get platform information summary
 * @returns {object} Complete platform information
 */
export const getPlatformInfo = () => {
  return {
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isMobile: isMobile(),
    isDesktop: isDesktop(),
    isTablet: isTablet(),
    isPhone: isPhone(),
    isSafari: isSafari(),
    isChrome: isChrome(),
    isFirefox: isFirefox(),
    isEdge: isEdge(),
    supportsPWA: supportsPWAInstallation(),
    isRunningAsPWA: isRunningAsPWA(),
    supportsTouch: supportsTouch(),
    orientation: getDeviceOrientation(),
    screen: getScreenDimensions(),
    userAgent: getUserAgent(),
    iosVersion: getIOSVersion(),
    androidVersion: getAndroidVersion(),
    recommendedBrowser: getRecommendedBrowser(),
  }
}

/**
 * Get a user-friendly device name based on user agent
 * @returns {string} Device name
 */
export const getDeviceName = () => {
  const userAgent = getUserAgent()
  if (/iPhone/.test(userAgent)) return 'iPhone'
  if (/iPad/.test(userAgent)) return 'iPad'
  if (/Macintosh/.test(userAgent)) return 'Mac'
  if (/Android/.test(userAgent)) return 'Android Device'
  if (/Windows/.test(userAgent)) return 'Windows PC'
  if (/Linux/.test(userAgent)) return 'Linux PC'
  return 'Unknown Device'
}

/**
 * Get a persistent unique device ID (UUID stored in localStorage)
 * @returns {string} Device ID
 */
export const getDeviceId = () => {
  let deviceId = localStorage.getItem('device_id')
  if (!deviceId) {
    if (window.crypto && window.crypto.randomUUID) {
      deviceId = window.crypto.randomUUID()
    } else {
      // Fallback for browsers without crypto.randomUUID
      deviceId = 'xxxxxxxxyxxxxyxxxyxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0,
          v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
      })
    }
    localStorage.setItem('device_id', deviceId)
  }
  return deviceId
}
/**
 * Checks if the current view is a mobile view and commits the result to the 'layout' Vuex module.
 * @returns {boolean} True if the current view is a mobile view.
 */

export const checkIsTabView = async () => {
  const { default: store } = await import('@src/state/store.js')
  const isTabView = window.matchMedia('(max-width: 767px)').matches
  store.commit('layout/SET_TAB_VIEW', isTabView)
  return isTabView
}
export const checkIsMobileView = async () => {
  const { default: store } = await import('@src/state/store.js')
  const isMobileView = window.matchMedia('(max-width: 640px)').matches
  store.commit('layout/SET_MOBILE_VIEW', isMobileView)
  return isMobileView
}
export const checkIsLaptopView = async () => {
  const { default: store } = await import('@src/state/store.js')
  const isLaptopView = window.matchMedia('(max-width: 1279px)').matches
  store.commit('layout/SET_LAPTOP_VIEW', isLaptopView)
  return isLaptopView
}

export default {
  getUserAgent,
  isCapacitor,
  isIOS,
  isAndroid,
  isMobile,
  isDesktop,
  isTablet,
  isPhone,
  getIOSVersion,
  getAndroidVersion,
  isSafari,
  isChrome,
  isFirefox,
  isEdge,
  supportsPWAInstallation,
  isRunningAsPWA,
  getInstallInstructions,
  getRecommendedBrowser,
  supportsTouch,
  getDeviceOrientation,
  isPortrait,
  isLandscape,
  getScreenDimensions,
  hasHighDPIDisplay,
  getPlatformInfo,
  getDeviceName,
  getDeviceId,
  checkIsTabView,
  checkIsMobileView,
}
